type ApiResponseErrorPayload = {
  status?: number | null;
  error?: string;
  data?: unknown;
};

type ApiErrorLike = {
  code?: string;
  message?: string;
  apiResponse?: ApiResponseErrorPayload;
  response?: {
    status?: number;
    data?: unknown;
  };
};

const BACKEND_UNAVAILABLE_MESSAGE =
  'No pudimos conectarnos con el servidor. Intentá nuevamente en unos momentos.';

const PERMISSION_DENIED_MESSAGE =
  'No tenés permisos para realizar esta acción. Si el problema continúa, volvé a iniciar sesión.';

const GENERIC_LOGIN_MESSAGE = 'Email o contraseña incorrecto/a';
const LOGIN_FORBIDDEN_MESSAGE =
  'No pudimos iniciar sesión con este usuario. Si el problema continúa, contactá soporte.';
const GENERIC_SINIESTRO_MESSAGE = 'No se pudo agregar el siniestro. Intente nuevamente.';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getApiErrorLike = (error: unknown): ApiErrorLike => (
  isRecord(error) ? error as ApiErrorLike : {}
);

const getApiErrorStatus = (error: unknown): number | null => {
  const apiError = getApiErrorLike(error);
  return apiError.apiResponse?.status ?? apiError.response?.status ?? null;
};

const getStringMessage = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;

  const trimmedValue = value.trim();
  return trimmedValue || undefined;
};

const getFirstMessageFromArray = (value: unknown): string | undefined => {
  if (!Array.isArray(value)) return undefined;

  for (const item of value) {
    const itemMessage = getStringMessage(item) ?? extractPayloadMessage(item);
    if (itemMessage) return itemMessage;
  }

  return undefined;
};

const isGenericBackendMessage = (message: string): boolean =>
  /check ['"]?errors['"]? property/i.test(message) ||
  /^invalid body/i.test(message);

function extractPayloadMessage(payload: unknown): string | undefined {
  const directMessage = getStringMessage(payload);
  if (directMessage) return directMessage;

  if (!isRecord(payload)) return undefined;

  const message =
    payload.message ??
    payload.error ??
    payload.errorMessage ??
    payload.detail ??
    payload.msg ??
    payload.mensaje;

  const messageText =
    getStringMessage(message) ??
    extractPayloadMessage(message) ??
    getFirstMessageFromArray(message);

  const arrayError = getFirstMessageFromArray(payload.errors);
  if (arrayError && (!messageText || isGenericBackendMessage(messageText))) {
    return arrayError;
  }

  if (isRecord(payload.errors)) {
    for (const errorValue of Object.values(payload.errors)) {
      const errorMessage =
        getStringMessage(errorValue) ??
        getFirstMessageFromArray(errorValue) ??
        extractPayloadMessage(errorValue);

      if (errorMessage && (!messageText || isGenericBackendMessage(messageText))) {
        return errorMessage;
      }
    }
  }

  if (messageText) return messageText;

  return undefined;
}

const getApiErrorDetail = (error: unknown): string => {
  if (typeof error === 'string') return error.trim();

  const apiError = getApiErrorLike(error);
  const responseDetail =
    extractPayloadMessage(apiError.apiResponse?.data) ??
    extractPayloadMessage(apiError.response?.data) ??
    apiError.apiResponse?.error ??
    apiError.message ??
    '';

  return responseDetail.trim();
};

export const getApiErrorMessage = (
  error: unknown,
  fallbackMessage = 'Ocurrió un error inesperado.',
): string => getApiErrorDetail(error) || fallbackMessage;

const getSiniestroExternalErrorDetail = (error: unknown): string | undefined => {
  const apiError = getApiErrorLike(error);
  const payload = apiError.apiResponse?.data ?? apiError.response?.data;

  if (!isRecord(payload) || payload.type !== 'externo') return undefined;

  return extractPayloadMessage(payload.detail);
};

const isBackendUnavailableError = (error: unknown): boolean => {
  const apiError = getApiErrorLike(error);
  const status = getApiErrorStatus(error);
  const code = apiError.code;

  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (!status && (code === 'ERR_NETWORK' || apiError.message === 'Network Error')) return true;

  return Boolean(status && status >= 500);
};

const isPermissionDeniedError = (error: unknown): boolean => {
  const status = getApiErrorStatus(error);
  return status === 401 || status === 403;
};

const isForbiddenError = (error: unknown): boolean => getApiErrorStatus(error) === 403;

export const getLoginErrorMessage = (error: unknown): string => {
  if (isBackendUnavailableError(error)) return BACKEND_UNAVAILABLE_MESSAGE;
  if (isForbiddenError(error)) return LOGIN_FORBIDDEN_MESSAGE;
  if (isPermissionDeniedError(error)) return GENERIC_LOGIN_MESSAGE;

  const detail = getApiErrorDetail(error);
  return detail || GENERIC_LOGIN_MESSAGE;
};

export const getSiniestroCreateErrorMessage = (error: unknown): string => {
  const externalDetail = getSiniestroExternalErrorDetail(error);
  if (externalDetail) return externalDetail;

  const detail = getApiErrorDetail(error);
  if (detail) return detail;

  if (isBackendUnavailableError(error)) return BACKEND_UNAVAILABLE_MESSAGE;

  if (isPermissionDeniedError(error)) return PERMISSION_DENIED_MESSAGE;
  return GENERIC_SINIESTRO_MESSAGE;
};

export const getApiFailureStatus = getApiErrorStatus;
